import InformationCard from "./InformationCard";

import { useBooleanState } from "~/common/hooks/useBooleanState";
import UserInfoModal from "./UserInfoModal";
import { AlertTitle, Box, Button, TextField, styled } from "~/common/mui";
import { Link } from "~/common/components/Link";
import { type Dispatch, forwardRef, type SetStateAction } from "react";
import { useSalutationOptions } from "~/domains/referenceData/hooks/useFormOptions";
import { type DataObject } from "~/pages/B2CRequestPage/B2CRequestPage";
import { redirectToUrl } from "~/utils/url/urlUtils";
import { CART_STEP_2 } from "~/utils/url/staticUrls";
import { updateUserInfo } from "../api/updateUserInfo";
import { transformUserInfoData } from "../utils";
import { SALUTATION_CODES, type UserInfoData } from "../types";
import { t } from "i18next";
import { useIsMinDesktopSize, useIsMinTabletSize } from "~/common/hooks/useMediaQuery";
import { Alert } from "~/common/mui/Alert/Alert";
import { Checkboxes } from "./Checkboxes";
import { userIsLoggedIn } from "~/domains/user/utils/roles";
import { submitGuestCheckoutForm } from "../../B2CCart/components/GuestCheckoutDialog/api/guestCheckoutSubmitForm";
import { Controller } from "react-hook-form";

const THEME_SPACING_UNIT = 8;
const themeSpacing2_25 = `${2.25 * THEME_SPACING_UNIT}px`;

const Root = styled("div")(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "1fr",
  gridTemplateRows: "repeat(6, auto)",
  gridColumnGap: 0,
  gridRowGap: theme.spacing(2),
  [theme.breakpoints.up("tablet")]: {
    gridTemplateColumns: "repeat(6, 1fr)",
    gridTemplateRows: "repeat(3, auto)",
    gridColumnGap: theme.spacing(4),
  },
  [theme.breakpoints.up("desktop-s")]: {
    gridTemplateColumns: "repeat(4, 1fr)",
    gridTemplateRows: "repeat(2, auto)",
  },
  "& .card": {
    display: "flex",
    flexFlow: "column",
    gridColumn: "span 2",
    [theme.breakpoints.up("desktop-s")]: {
      gridColumn: "unset",
    },
  },
}));

const CheckBoxArea = styled("div", { shouldForwardProp: (p) => p !== "reverse" })<{ reverse?: boolean }>(
  ({ theme, reverse }) => ({
    display: "flex",
    flexFlow: "column",
    justifyContent: "space-between",
    ...(reverse && { flexDirection: "column-reverse", gap: theme.spacing(2) }),
    [theme.breakpoints.up("tablet")]: {
      gridArea: "2 / 1 / 2 / 7",
    },
    [theme.breakpoints.up("desktop-s")]: {
      gridArea: "unset",
    },
  }),
);

const CheckboxContent = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2),
}));

const MandatoryMessage = styled("div")(({ theme }) => ({
  marginLeft: theme.spacing(3),
}));

const Footer = styled("div")(({ theme }) => ({
  gridArea: "6 / 1 / 6 / 1",
  [theme.breakpoints.up("tablet")]: {
    gridArea: "3 / 4 / 3 / 7",
  },
  [theme.breakpoints.up("desktop-s")]: {
    gridArea: "2 / 4 / 2 / 4",
  },
}));

const Label = styled("span")(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontWeight: "normal",
}));

const CardContainer = styled("div")(({ theme }) => ({
  display: "flex",
  flexFlow: "column",
  gap: theme.spacing(2),
  flex: 1,
}));

const ContactContainer = styled("div")(({ theme }) => ({
  display: "flex",
  flexFlow: "column",
  gap: theme.spacing(0.5),
  wordBreak: "break-all",
}));

const FlexAuto = styled("div")({ flex: "auto" });

const BottomMargin = styled("div")(({ theme }) => ({
  marginBottom: "unset",
  [theme.breakpoints.up("tablet")]: {
    marginBottom: theme.spacing(2.25),
  },
}));

interface InformationContainerProps {
  className?: string;
  onButtonClick: () => void;
  dataObject: DataObject;
  setDataObject: Dispatch<SetStateAction<DataObject>>;
  hasAllUserInfo: boolean;
  marketingConsent: boolean;
  cartId?: string;
  guestCheckoutContext?: string;
  onGuestCheckoutContextChange?: (context: string) => void;
}

const HIDDEN_SALUTATIONS = [SALUTATION_CODES.DIVERSE, SALUTATION_CODES.PREFER_NOT_TO_SAY];

const InformationContainer = forwardRef<HTMLDivElement, InformationContainerProps>(
  (
    {
      className,
      onButtonClick,
      dataObject,
      setDataObject,
      hasAllUserInfo,
      marketingConsent,
      cartId,
      onGuestCheckoutContextChange,
    },
    ref,
  ) => {
    const { val: userInfoModalOpen, setFalse: closeUserInfoModal, setTrue: openUserInfoModal } = useBooleanState(false);

    const isDesktop = useIsMinDesktopSize();
    const isTablet = useIsMinTabletSize();

    const salutationOptions = useSalutationOptions();

    const handleUserSalutation = (code: string) => {
      if (HIDDEN_SALUTATIONS.includes(code as SALUTATION_CODES)) {
        return "";
      } else {
        return salutationOptions.find((option) => option.value === dataObject.userDetails.salutationCode)?.label;
      }
    };

    const userSalutation = handleUserSalutation(dataObject.userDetails.salutationCode);

    const handleFormSubmit = (data: UserInfoData) => {
      setDataObject({ ...dataObject, userDetails: data });
      if (data.updateUser) {
        if (userIsLoggedIn()) {
          updateUserInfo(transformUserInfoData(data)).then(() => closeUserInfoModal());
        } else {
          submitGuestCheckoutForm(cartId ?? "", {
            ...transformUserInfoData(data),
            companyName: data.company,
            email: data.email,
            line2: data.additionalLine1 ?? "",
          }).then((response) => {
            const context = response.headers.get("X-Schaeffler-Guest-Checkout-Context");
            if (context) {
              onGuestCheckoutContextChange?.(context);
            }
            closeUserInfoModal();
          });
        }
      } else {
        closeUserInfoModal();
      }
    };

    const goToStep2 = () => redirectToUrl(CART_STEP_2);

    const errorMessage = !hasAllUserInfo && (
      <Alert severity="error">
        <AlertTitle>{t("request.missingInfoErrorTitle")}</AlertTitle>
        {t("request.missingInfoErrorMessage")}
      </Alert>
    );

    return (
      <Root className={className}>
        <InformationCard className="card" title={t("request.informationLabel")}>
          <TextField
            value={dataObject.requestNumber}
            onChange={(e) => setDataObject({ ...dataObject, requestNumber: e.target.value })}
            placeholder={t("request.number")}
          />
        </InformationCard>
        <InformationCard
          error={!hasAllUserInfo}
          className="card"
          title={t("request.userInformation")}
          editAction={openUserInfoModal}
        >
          <CardContainer>
            <div>
              <div>
                <Label>{t("request.information.name")}</Label>
              </div>
              <div>
                <b>{dataObject.userDetails.company}</b>
              </div>
              <div>{`${userSalutation} ${dataObject.userDetails.firstName} ${dataObject.userDetails.lastName}`}</div>
            </div>

            <FlexAuto>
              <div>
                <Label>{t("page.completeRegistration.address")}</Label>
              </div>
              {/*
              Line 1 contains house number and street
              Additional address line (displayed after Line1)
              Line2 contains the city, region and postalcode
              */}
              <div>{`${dataObject.userDetails.line1}`}</div>
              {dataObject.userDetails.additionalLine1 && <div>{`${dataObject.userDetails.additionalLine1}`}</div>}
              <div>{`${dataObject.userDetails.line2}`}</div>

              <div>{dataObject.userDetails.country && t(`country.${dataObject.userDetails.country}translation`)}</div>
            </FlexAuto>
            <div>
              <ContactContainer>
                <Label>
                  <span>{t("request.contact")}</span>
                </Label>

                <div>{dataObject.userDetails.phone}</div>
                <div>{dataObject.userDetails.email}</div>
                <Box sx={{ mb: dataObject.salesPartnerData.email ? 0 : themeSpacing2_25 }} />
              </ContactContainer>
            </div>
          </CardContainer>
        </InformationCard>

        {!isTablet && errorMessage}

        <InformationCard className="card" title={t("request.salesPartnerInformation")} editAction={goToStep2}>
          <CardContainer>
            <div>
              <Label>
                <span>{t("request.information.name")}</span>
              </Label>
              <BottomMargin>
                <b>{dataObject.salesPartnerData.name}</b>
              </BottomMargin>
            </div>

            <FlexAuto>
              <Label>
                <span>{t("page.completeRegistration.address")}</span>
              </Label>
              <div>{`${dataObject.salesPartnerData.line1}`}</div>
              <div>{`${dataObject.salesPartnerData.line2 ?? ""}`}</div>
              <div>{t(`country.${dataObject.salesPartnerData.country}translation`)}</div>
            </FlexAuto>

            {(dataObject.salesPartnerData.phone || dataObject.salesPartnerData.email) && (
              <ContactContainer>
                <div>
                  <Label>{t("request.contact")}</Label>
                </div>

                <Box sx={{ mb: !dataObject.salesPartnerData.phone ? themeSpacing2_25 : 0 }}>
                  {dataObject.salesPartnerData.phone}
                </Box>

                <div>{dataObject.salesPartnerData.fax}</div>

                <Link href={`mailto:${dataObject.salesPartnerData.email}`}>{dataObject.salesPartnerData.email}</Link>
              </ContactContainer>
            )}
          </CardContainer>
        </InformationCard>

        <CheckBoxArea ref={ref} reverse={!hasAllUserInfo && !isDesktop}>
          <CheckboxContent>
            <Controller
              name="tcAgreement"
              rules={{ required: true }}
              render={({ field, fieldState: { error } }) => (
                <>
                  <Checkboxes.TermsConditions
                    checked={field.value}
                    error={!!error}
                    onChange={(e) => field.onChange(e.target.checked)}
                  />

                  {error && <Alert severity="error">{t("request.tcAgree")}</Alert>}
                </>
              )}
            />
            <Controller
              name="ppAgreement"
              rules={{ required: true }}
              render={({ field, fieldState: { error } }) => (
                <>
                  <Checkboxes.PrivacyPolicy
                    checked={field.value}
                    error={!!error}
                    onChange={(e) => field.onChange(e.target.checked)}
                  />
                  {error && <Alert severity="error">{t("request.privacyAgree")}</Alert>}
                </>
              )}
            />
            {!marketingConsent && (
              <Controller
                name="userAgreement"
                render={({ field, fieldState: { error } }) => (
                  <Checkboxes.MarketingConsent
                    checked={field.value}
                    error={!!error}
                    onChange={(e) => field.onChange(e.target.checked)}
                  />
                )}
              />
            )}

            <MandatoryMessage>{t("mandatory")}</MandatoryMessage>
          </CheckboxContent>
          {isTablet && errorMessage}
        </CheckBoxArea>
        <Footer>
          <Button disabled={!hasAllUserInfo} onClick={onButtonClick} fullWidth>
            {t("page.cart.b2c.process.step.3")}
          </Button>
        </Footer>

        {userInfoModalOpen && (
          <UserInfoModal
            values={dataObject.userDetails}
            formSubmit={handleFormSubmit}
            closeModal={closeUserInfoModal}
          />
        )}
      </Root>
    );
  },
);

export default InformationContainer;
